import { useCallback, useMemo } from 'react';
import { parseAsInteger, parseAsString, useQueryStates } from 'nuqs';
import { GroupBy, ReportFilters, ReportTab, TargetType } from '../types';
import { defaultRange } from '../utils/period';

export const REPORT_TABS = ['tong-quan', 'theo-sach', 'theo-lo', 'theo-nguoi-dung'] as const satisfies readonly ReportTab[];

const GROUP_BY_VALUES = ['day', 'week', 'month'] as const satisfies readonly GroupBy[];
const TYPE_VALUES = ['all', 'book', 'collection'] as const satisfies readonly TargetType[];

type FilterPatch = Partial<ReportFilters>;

// Không dùng parseAsStringLiteral: TypeScript 4.9 của dự án không đọc được khai báo `<const T>` của nuqs.
// Giá trị lạ trên URL (tab=abc, group_by=year) rơi về mặc định.
const pick = <T extends string>(list: readonly T[], value: string, fallback: T): T =>
  (list as readonly string[]).includes(value) ? (value as T) : fallback;

// Khoá chuỗi không có mặc định: vắng trong URL nghĩa là không lọc
const OPTIONAL_KEYS = ['school_id', 'class_id', 'book_id', 'collection_id', 'batch_id', 'q'] as const;

export function useReportFilters() {
  // Chốt một lần để mặc định không đổi giữa các lần render (qua nửa đêm)
  const range = useMemo(() => defaultRange(new Date()), []);

  const parsers = useMemo(
    () => ({
      tab: parseAsString.withDefault('tong-quan'),
      from: parseAsString.withDefault(range.from),
      to: parseAsString.withDefault(range.to),
      school_id: parseAsString,
      class_id: parseAsString,
      book_id: parseAsString,
      collection_id: parseAsString,
      batch_id: parseAsString,
      q: parseAsString,
      group_by: parseAsString.withDefault('day'),
      type: parseAsString.withDefault('all'),
      page: parseAsInteger.withDefault(1),
    }),
    [range]
  );

  const [state, setState] = useQueryStates(parsers);

  const filters: ReportFilters = useMemo(() => {
    const f: ReportFilters = {
      from: state.from,
      to: state.to,
      group_by: pick(GROUP_BY_VALUES, state.group_by, 'day'),
      type: pick(TYPE_VALUES, state.type, 'all'),
      page: state.page < 1 ? 1 : state.page,
    };
    OPTIONAL_KEYS.forEach((key) => {
      const value = state[key];
      if (value) f[key] = value;
    });
    return f;
  }, [state]);

  const setFilters = useCallback(
    (patch: FilterPatch) => {
      // undefined nghĩa là xoá; nuqs coi undefined là "giữ nguyên" nên đổi sang null
      const next: Record<string, unknown> = {};
      (Object.keys(patch) as (keyof FilterPatch)[]).forEach((key) => {
        // Cỡ trang cố định, không đưa lên URL
        if (key === 'per_page') return;
        next[key] = patch[key] === undefined ? null : patch[key];
      });
      // Lớp cũ không thuộc trường mới
      if ('school_id' in patch) next.class_id = null;
      // Bộ lọc đổi thì trang cũ có thể không còn tồn tại
      if (!('page' in patch)) next.page = 1;
      return setState(next as Parameters<typeof setState>[0]);
    },
    [setState]
  );

  const setTab = useCallback(
    (tab: ReportTab) => setState({ tab, page: 1 }),
    [setState]
  );

  const clearFilters = useCallback(
    () =>
      setState({
        from: null,
        to: null,
        school_id: null,
        class_id: null,
        book_id: null,
        collection_id: null,
        batch_id: null,
        q: null,
        group_by: null,
        type: null,
        page: null,
      }),
    [setState]
  );

  const tab = pick(REPORT_TABS, state.tab, 'tong-quan');

  return { tab, setTab, filters, setFilters, clearFilters };
}
